import { Camera } from "../clases/Camera";
import { Circle } from "../clases/Circle";
import { ComposedMetrics } from "../clases/ComposedMetrics";
import { Line } from "../clases/Line";
import { ShapeBounding } from "../clases/ShapeBounding";
import { SimulationValues } from "../clases/SimulationValues";
import { Values } from "../clases/Values";
import { Anchor } from "../enums/Anchor";
import { Shape } from "../enums/Shape";
import { BoxMetrics } from "../interfaces/BoxMetrix";
import { CanvasColorTheme } from "../interfaces/CanvasColorTheme";
import { IContainer } from "../interfaces/Container";
import { IField } from "../interfaces/Field";
import { HeatMap } from "../interfaces/HeatMap";
import { HyperFlowsCategory } from "../interfaces/HyperFlows";
import { DBItem } from "../interfaces/Record";
import { ShapeIcon, UIPropierties } from "../interfaces/ShapeIcon";
import { SimulationGlobalAverage } from "../interfaces/Simulation";
import { COLOR_03, COLOR_05, COLOR_08, COLOR_10, COLOR_14, COLOR_20, COLOR_21, GetThemeByName } from "../styles/Canvas";
import { getShapeWithId, parentExistOnList } from "../utils/ContainerUtils";
import { convertIntoShapeEnum } from "../utils/utils";

export abstract class AbstractShape {
    x: number = 0;
    y: number = 0;
    vx: number = 0;
    vy: number = 0;
    shadowPos: Values = new Values(0,0);
    originalPos: Values = new Values(0,0);
    width: number = 20000;
    height: number = 20000;
    id: string;
    name: string = "";
    fill: boolean = true;
    children: AbstractShape[] = [];
    zIndex: number;
    parent?: AbstractShape | undefined;
    parentId?: string;
    layout: string = 'free';
    focus: boolean = false;
    dbData!: DBItem;
    dbPropierties!: any;
    fields: IField[] = [];

    gridSizeX: number = 5;
    gridSizeY: number = 5;
    spacing: number = 20;
    boxSize: number = 70;
    boxSpacing: number = 7;

    relationType: string | null = null;
    // labels: string[] = [];

    radius: number = 5;
    textPadding: number = 15;
    headerPaddingWidth: number = 10;
    headerPaddingHeight: number = 10;
    textWidth: number = 0;
    dottedSpacing: number = 0;
    realDottedSpacing: number = 0;
    ownScale: number = 0;
    realFontSize: number = 0;
    currentBounding: ShapeBounding | null = null;

    totalHeaderHeight: number = 0;

    isDragging: boolean = false;
    isHover: boolean = false;

    group!: string;

    shape: Shape = Shape.CIRCLE;
    icon!: ShapeIcon[];
    theme_color: CanvasColorTheme = COLOR_10;
    gray_theme: CanvasColorTheme = COLOR_21;
    container_theme!: CanvasColorTheme;

    isContainer: boolean = false;

    metrics!: BoxMetrics | undefined;
    composeMetrics!: ComposedMetrics | undefined;

    simulationValues: SimulationValues | null = null;
    simulationAverage: SimulationGlobalAverage | null | undefined;

    showMetrics: boolean = true;
    setShowMetrics(showMetrics: boolean){
        this.showMetrics = showMetrics;
    }

    isInView: boolean = false;

    showShadow: boolean = false;

    hyperFlows: HyperFlowsCategory[] | null | undefined = undefined;

    heatmap: HeatMap | undefined;
    heatmapTheme: CanvasColorTheme = this.gray_theme;
    isHeatMap: boolean = false;

    container_shapes: Shape[]=[
        Shape.RECTANGLE,
        Shape.SWIM_LANE_CONTAINER,
        Shape.DOTTED_AREA,
        Shape.RECTANGLE_AREA,
        Shape.SIMPLE_AREA,
        Shape.SWIM_LANE_DOTTED
    ];

    hidePropierties: string[] = [
        "uiProperties",
        "group"
    ];

    constructor(
        id: string,
        zIndex: number,
        parent: AbstractShape | undefined = undefined,
    ) {
        this.id = id;
        this.zIndex = zIndex;
        this.parent = parent;
    }

    setRelationType(type: string) {
        this.relationType = type;
    }

    getRelationType() {
        return this.relationType;
    }

    // setLabels(labels: string[]) {
    //     this.labels = labels;
    // }

    getLabels() {
        return this.dbData.labels[0];
    }

    getAllLabels(){
        return this.dbData.labels;
    }

    setHover(hover: boolean){
        this.isHover = hover;
    }

    getHover(): boolean{
        return this.isHover;
    }

    setMetrics(metrics: BoxMetrics | undefined, composeMetrics: ComposedMetrics|undefined){
        this.composeMetrics = composeMetrics;
        this.metrics = metrics;
        if(composeMetrics){
            this.restoreTheme();
        }
    }

    setHeatmap(heatmap: HeatMap | undefined){
        this.heatmap = heatmap;
        if(!heatmap){
            this.restoreTheme();
            return;
        }
        this.chooseHeatMapTheme();
    }

    chooseHeatMapTheme(){
        switch (this.heatmap!.isBottleneck) {
            case "green":
                this.heatmapTheme = COLOR_08;
                break;
            case "yellow":
                this.heatmapTheme = COLOR_03;
                break;
            case "red":
                this.heatmapTheme = COLOR_03;
                break;
            case "purple":
                this.heatmapTheme = COLOR_14;
                break;
            case "black":
                this.heatmapTheme = COLOR_20;
                break;
        }
    }

    toggleHeatmapTheme(show: boolean){
        this.isHeatMap = show;
        if(!show){
            this.restoreTheme();
            return;
        }
        if(this.heatmap){
            this.setTheme(this.heatmapTheme);
            return;
        }
        this.setGrayTheme();
    }

    setGrayTheme(){
        this.setTheme(this.gray_theme);
    }

    restoreTheme(){
        if(this.isHeatMap) return;
        this.setTheme(this.container_theme);
    }

    setDBPropierties(properties: any) {
        this.dbPropierties = properties;
        this.name = properties.name || properties.title;
        if (this.name) this.name = this.name.toLocaleUpperCase();
        
        this.fields = [];
        for (const key in this.dbPropierties) {
            if(this.hidePropierties.includes(key)) continue;
            this.fields = [...this.fields, { name: key, value: this.dbPropierties[key] }];
        }
        this.fields = this.fields.reverse();

        let ui: any = properties.uiProperties;
        if (!ui) return;
        let parsed = JSON.parse(ui) as UIPropierties;
        if (parsed.image){
            this.setIcon(parsed);
        }
        if (parsed.type && !this.isContainer) {
            // this.shape = convertIntoShapeEnum(parsed.type);
            this.setShape(convertIntoShapeEnum(parsed.type));
            this.currentBounding = null;
            setTimeout(() => {
                this.setSize(this.width, this.height);
            }, 0);
        }
        if(parsed.group) this.group = parsed.group;
    }

    setDBData(data: DBItem) {
        this.dbData = data;
        this.setDBPropierties(data.properties);
    }

    setIcon(properties: UIPropierties){
        this.icon = []; //check this one
        this.icon.push(JSON.parse(properties.image as string) as ShapeIcon);
    }

    setContainer(container: IContainer) {
        this.x = container.x;
        this.y = container.y;
        // this.width = container.width;
        // this.height = container.height;
        this.fill = container.fill;
        this.layout = container.layout;
        this.theme_color = GetThemeByName(container.color);
        this.container_theme = GetThemeByName(container.color);
        this.textWidth = container.textWidth || 0;
        this.dottedSpacing = container.dottedSpacing || 0;
        const enumValue = convertIntoShapeEnum(container.shape);
        this.shape = enumValue;
        this.setSize(container.width, container.height);
        this.calculateGridSize();
        this.isContainer = true;
    }

    setPosition(x: number, y: number) {
        let newX = Math.ceil(x);
        let newY = Math.ceil(y);
        if(this.parent){
            this.clampParentBounds(newX, newY);
            return;
        }
        this.x = newX;
        this.y = newY;
    }

    getPosition(): Values {
        if(this.currentBounding) return new Values(this.currentBounding.x, this.currentBounding.y);
        return new Values(this.x, this.y);
    }

    move(dx: number, dy: number) {
        const newX = Math.ceil(this.x + dx);
        const newY = Math.ceil(this.y + dy);

        if (this.parent) {
            this.clampParentBounds(newX, newY);
        } else {
            // No parent; move freely
            this.x = newX;
            this.y = newY;
            this.showShadow = false;
            this.originalPos = new Values(this.x, this.y);
        }
        this.shadowPos = new Values(newX, newY);
    }

    clampParentBounds(newX: number, newY: number){
        // Ensure the box does not move outside the parent's bounds
        const parentBounds = {
            left: 0,
            top: 0,
            right: this.parent!.width - this.width,
            bottom: this.parent!.height - this.height,
        };

        if(
            newX > parentBounds.right ||
            newX < parentBounds.left || 
            newY > parentBounds.bottom || 
            newY < parentBounds.top
        ){
            this.showShadow = true;
        }else{
            this.showShadow = false;
        }

        // Clamp the new position within the parent's bounds
        this.x = Math.max(parentBounds.left, Math.min(newX, parentBounds.right));
        this.y = Math.max(parentBounds.top, Math.min(newY, parentBounds.bottom));
        this.originalPos = new Values(this.x, this.y);
    }

    setSize(width: number, height: number) {
        // A negative size produces an inverted resize box and breaks hit
        // testing. Keep every shape's dimensions positive at this boundary.
        width = Math.max(1, width);
        height = Math.max(1, height);

        switch (this.shape) {
            case Shape.RECTANGLE:
                this.width = Math.ceil(width);
                this.height = Math.ceil(height);
                break;
            case Shape.SWIM_LANE_CONTAINER:
                this.width = Math.ceil(width);
                this.height = Math.ceil(height);
                break;
            case Shape.CIRCLE:
                this.sizeSameWidth(width);
                break;
            case Shape.HEXAGON:
                this.sizeSameWidth(width);
                break;
            case Shape.RHOMBUS:
                this.sizeSameWidth(width);
                break;
            case Shape.INFO_RECTANGLE:
                this.width = Math.ceil(width);
                this.height = Math.ceil(width / 1.5);
                break;
            case Shape.SIMPLE_RECTANGLE:
                this.sizeSameWidth(width);
                break;
            case Shape.SIMPLE_CIRCLE:
                this.sizeSameWidth(width);
                break;
            case Shape.SIMPLE_RHOMBUS:
                this.sizeSameWidth(width);
                break;
            case Shape.SIMPLE_HEXAGON:
                this.sizeSameWidth(width);
                break;
            case Shape.PEOPLE_HEX:
                this.sizeSameWidth(width);
                break;
            default:
                this.width = Math.ceil(width);
                this.height = Math.ceil(height);
                break;
        }

        this.headerPaddingWidth = this.width / 25;
        this.headerPaddingHeight = this.height / 25;
        // this.radius = ((this.width + this.height) / 2) / 20;

        this.calculateGridSize();
    }

    sizeSameWidth(width: number) {
        this.width = Math.ceil(width);
        this.height = Math.ceil(width);
    }

    shouldResize(point: Values, list: AbstractShape[]) {
        let offset = this.calculateAllOffsets(list);
        let compareX = this.x + this.width + offset.x - this.width / 10;
        let compareY = this.y + this.height + offset.y - this.width / 10;
        return point.x > compareX && point.y > compareY;
    }

    getSize() {
        return { x: this.width, y: this.height };
    }

    setFill(fill: boolean) {
        this.fill = fill;
    }

    toggleFill() {
        if(!this.container_shapes.some((shape: Shape)=> shape === this.shape)) return;
        this.fill = !this.fill;
    }

    getGroup(){
        return this.group;
    }

    setGroup(group: string){
        this.group = group;
    }

    getAllParents(item: AbstractShape, compare: AbstractShape[], parents: AbstractShape[] = []): AbstractShape[] {
        if (!item) return parents;
        if (!item.parentId) return parents;
        let parent = getShapeWithId(item.parentId, compare);
        if (parent) {
            parents = [...parents, parent];
            return this.getAllParents(parent, compare, parents);
        }
        return parents;
    }

    getAllParentsSelf(compare: AbstractShape[]) {
        return this.getAllParents(this, compare);
    }

    calculateAllOffsets(compare: AbstractShape[]) {
        let offsetX = 0;
        let offsetY = 0;
        let parents = this.getAllParentsSelf(compare);
        for (const item of parents) {
            offsetX = offsetX + item.x;
            offsetY = offsetY + item.y;
        }
        return new Values(offsetX, offsetY);
    }

    findFilled(compare: AbstractShape[]): AbstractShape | null {
        let parents = this.getAllParents(this, compare);
        parents = parents.reverse();
        parents = [...parents, this];
        for (const i of parents) {
            if (!i) continue;
            if (i.fill) return i;
        }
        return this;
    }

    transformToContainer(): IContainer {
        return {
            x: this.x,
            y: this.y,
            width: this.width,
            height: this.height,
            id: this.id,
            dbId: (this.dbData) ? this.dbData.properties.__EDT_UUID__ : "removed from database",
            fill: this.fill,
            layout: this.layout,
            shape: this.shape,
            color: this.theme_color.name,
            textWidth: this.textWidth,
            dottedSpacing: this.dottedSpacing
        } as IContainer;
    }

    addChild(child: AbstractShape, mainItems?: AbstractShape[]) {
        let isContainer = this.container_shapes.some((shape: Shape)=> shape === this.shape)
        if (this.children.length <= 0 && !isContainer) {
            this.shape = Shape.RECTANGLE;
            this.setSize(this.width*2.5, this.height*2.5);
        }
        child.setParent(this);
        child.zIndex = this.zIndex + 1;
        this.children.push(child);
        if(mainItems){
            let parents = this.getAllParentsSelf(mainItems);
            parents = parents.reverse();
            parents = [...parents, this]
            this.checkTotalChildrenDimensions(parents[0]);
        }
    }

    checkTotalChildrenDimensions(shape: AbstractShape) {
        let maxX = shape.width;
        let maxY = shape.height;
        for (const child of shape.children) {
            shape.checkTotalChildrenDimensions(child);
            const childRight = child.x + child.width;
            const childBottom = child.y + child.height;
            if(childRight > maxX){
                maxX = childRight
            }
            if(childBottom > maxY){
                maxY = childBottom
            }
        }
        shape.setSize(maxX, maxY);
    }

    removeChild(child: AbstractShape){
        this.children = this.children.filter((i)=>i.id !== child.id);
    }

    containsPoint(px: number, py: number): boolean {
        return px >= this.x && px <= this.x + this.width && py >= this.y && py <= this.y + this.height;
    }

    getIsInView(){
        return this.isInView;
    }

    setIsInView(value: boolean){
        this.isInView = value;
    }

    setParent(parent: AbstractShape | undefined){
        this.parent = parent;
        this.parentId = parent?.id;
    }

    setFocus(focus: boolean = true){
        this.focus = focus;
    }

    setTheme(theme: CanvasColorTheme){
        this.theme_color = theme;
    }

    setShape(shape: Shape){
        this.shape = shape;
        if(!this.container_shapes.includes(shape)) this.fill = true;
        this.currentBounding = null;
        this.setSize(this.width, this.height);
    }

    toShapeBounding(){
        return new ShapeBounding(this.x, this.y, this.width, this.height);
    }

    setLayout(layout: string){
        this.layout = layout;
    }

    calculateGridSize(){
        let totalwidth = this.width + (this.spacing * 2);
        let totalheight = this.height + (this.spacing * 2);
        let totalboxSizeX = (this.boxSize + (this.boxSpacing * 4));
        let totalboxSizeY = (this.boxSize + (this.boxSpacing * 4));
        this.gridSizeX = Math.round(totalwidth / totalboxSizeX);
        this.gridSizeY = Math.round(totalheight / totalboxSizeY);
    }

    isSquares(){
        return (this.layout === 'squares')
    }

    isParentSquare(){
        if (this.parent) {
            return (this.parent.layout === 'squares');
        }
        return false;
    }

    findItemCenter(): Values{
        if(this.currentBounding) return new Values(this.currentBounding.width/2, this.currentBounding.height/2);
        return new Values((this.width / 2), (this.height / 2) );
    }

    calculateBoxLines(center: Values, bounding?: ShapeBounding): Line[]{
        let width = bounding ? bounding.width : this.width;
        let height = bounding ? bounding.height : this.height;
        let spacingW = bounding ? (bounding.width - this.width)/2 : 0;
        let spacingH = bounding ? (bounding.height - this.height)/2 : 0;
        let hwidth = Math.ceil(width/2);//- spacingW;
        let hHeight = Math.ceil(height/2);//+ spacingH;

        let top = center.y - hHeight;
        let bottom = center.y + hHeight;
        let left = center.x - hwidth;
        let right = center.x + hwidth;
        let points = [
            new Line(new Values(left, top), new Values(left, bottom)),
            new Line(new Values(left, top), new Values(right, top)),
            new Line(new Values(right, top), new Values(right, bottom)),
            new Line(new Values(left, bottom), new Values(right, bottom)),
        ];
        return points;
    }

    toCircle(center: Values){
        let bounding: ShapeBounding = this.currentBounding ? this.currentBounding : this.toShapeBounding();
        return new Circle(center.x, center.y, bounding.width / 2);
    }

    calculateHexPoints(bounding: ShapeBounding = this.toShapeBounding(), center: Values, fromCenter:boolean = false): Values[]{
        const points = [];
        for (let i = 0; i < 6; i++) {
            const angle = (Math.PI / 3) * i; // Divide 360° into 6 parts (60° per side)
            let px = 0;
            let py = 0;
            if(fromCenter){
                px = center.x + (bounding.width/2 * Math.cos(angle)); // X-coordinate of vertex
                py = center.y + (bounding.height/2 * Math.sin(angle)); // Y-coordinate of vertex
            }else{
                px = (center.x + (bounding.width / 2)) + (bounding.width/2 * Math.cos(angle)); // X-coordinate of vertex
                py = (center.y + (bounding.height / 2)) + (bounding.height/2 * Math.sin(angle)); // Y-coordinate of vertex
            }
            points.push(new Values(Math.ceil(px), Math.ceil(py)));
        }
        return points;
    }

    calculateLargeHexPoints(bounding: ShapeBounding = this.toShapeBounding()): Values[]{
        let spacePercent = 6.6 / 100;
        let spacing = bounding.width * spacePercent;
        const points = [
            new Values(bounding.x + spacing,bounding.y), // Top-left cut
            new Values(bounding.x + bounding.width - spacing, bounding.y), // Top-right cut
            new Values(bounding.x + bounding.width, bounding.y + bounding.height / 2), // Right middle
            new Values(bounding.x + bounding.width - spacing, bounding.y + bounding.height), // Bottom-right cut
            new Values(bounding.x + spacing, bounding.y + bounding.height), // Bottom-left cut
            new Values(bounding.x, bounding.y + bounding.height / 2), // Left middle
        ];
        return points;
    }

    calculateLargeHexPointFromCenter(bounding: ShapeBounding = this.toShapeBounding(), center: Values){
        const centerX = center.x;
        const centerY = center.y + bounding.y;
        const halfWidth = bounding.width / 2;
        const halfHeight = bounding.height / 2;
        const space = bounding.width * 0.066;

        return [
            { x: centerX - halfWidth + space, y: centerY - halfHeight },
            { x: centerX + halfWidth - space, y: centerY - halfHeight },
            { x: centerX + halfWidth, y: centerY },
            { x: centerX + halfWidth - space, y: centerY + halfHeight },
            { x: centerX - halfWidth + space, y: centerY + halfHeight },
            { x: centerX - halfWidth, y: centerY },
        ];
    }

    generatePointsLines(points: Values[]){
        const lines: Line[] = [];
        let last = null;
        for (let index = 0; index < points.length; index++) {
            const point = points[index];
            if(!last){
                // lines.push(new Line(point, points[index+1]));
                last = point;
                continue;
            }
            lines.push(new Line(last, point));
            last = point;
        }
        lines.push(new Line(points[points.length-1], points[0]));
        return lines;
    }

    calculateRhombusPoints(bounding: ShapeBounding = this.toShapeBounding(), pos: Values): Values[]{
        const centerX = pos.x + bounding.width / 2;
        const centerY = pos.y + bounding.height / 2;
        const points = [
            new Values(centerX, pos.y),
            new Values(pos.x + bounding.width, centerY),
            new Values(centerX, pos.y + bounding.height),
            new Values(pos.x, centerY),
        ];
        return points;
    }

    getShapeLines(center: Values){
        switch(this.shape){
            case Shape.RECTANGLE:
                return this.calculateBoxLines(center);
            case Shape.SWIM_LANE_CONTAINER:
                return this.calculateBoxLines(center);
            case Shape.HEXAGON:
                return this.generatePointsLines(this.calculateHexPoints(undefined, center, true));
            case Shape.RHOMBUS:
                let finalR = new Values(center.x - (this.width/2), center.y - (this.height/2));
                return this.generatePointsLines(this.calculateRhombusPoints(undefined, finalR));
            case Shape.SIMPLE_RECTANGLE:
                // let boundingR = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                // return this.calculateBoxLines(center, boundingR);

                let boundingR = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                return this.calculateBoxLines(center, boundingR);
            case Shape.SIMPLE_RHOMBUS:
                // let boundingRH = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                // let finalSR = new Values(center.x - (boundingRH.width/2), center.y - (boundingRH.height/2));
                // return this.generatePointsLines(this.calculateRhombusPoints(boundingRH, finalSR));
                
                let boundingRH = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                return this.calculateBoxLines(center, boundingRH);
            case Shape.SIMPLE_HEXAGON:
                // let boundingH = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                // return this.generatePointsLines(this.calculateHexPoints(boundingH, center, true));
                let boundingH = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                return this.calculateBoxLines(center, boundingH);
            case Shape.SIMPLE_CIRCLE:
                //this is a additional I implemented for temporal fixes, check later
                let boundingC = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                return this.calculateBoxLines(center, boundingC);
            case Shape.PEOPLE_HEX:
                let boundingPH = this.currentBounding ? this.currentBounding : this.toShapeBounding();
                return this.calculateBoxLines(center, boundingPH);

                // let boundingP = this.currentBounding ? this.currentBounding : this.toShapeBounding();

                // let reduceIcon = 1.5;
                // let heightRH = this.height / reduceIcon;
                // let marginY = this.height*(5/100);
                // let finalX = center.x - (this.width/2);
                // let finalY = center.y - ((this.height - heightRH)/2);
                // let subShape = new ShapeBounding(finalX, finalY + heightRH + marginY, this.width, this.height - heightRH);


                // let subShape = new ShapeBounding(this.x, heightRH + marginY, this.width, this.height - heightRH);
                // return [...this.generatePointsLines(this.calculateLargeHexPointFromCenter(subShape, center)), ...this.generatePointsLines(this.calculateHexPoints(boundingP, center, true))];
                // return this.generatePointsLines(this.calculateLargeHexPointFromCenter(subShape, center));
                // return this.generatePointsLines(this.calculateHexPoints(boundingP, center, true));
                // let points = this.calculateHexPoints(boundingP, center, true);
                // let points = this.calculateLargeHexPointFromCenter(undefined, center);

                // let points = this.calculateLargeHexPoints(subShape);
                // let lines = this.generatePointsLines(points);
                // console.log(points);
                // console.log(lines);

                // return [...lines, ...this.generatePointsLines(this.calculateHexPoints(boundingP, center, true))];
                // return lines;

                // return this.generatePointsLines(this.calculateHexPoints(boundingP, center, true))
            default:
                return this.calculateBoxLines(center);
        }
    }

    getRectangleAnchorPoints(position: Values): Values[] {
        let bounds = this.currentBounding ? this.currentBounding : this.toShapeBounding();
        // Returns [top, right, bottom, left] anchor points
        const cx = position.x + bounds.width / 2;
        const cy = position.y + bounds.height / 2;
        return [
            new Values(cx, position.y), // top
            new Values(position.x + bounds.width, cy), // right
            new Values(cx, position.y + bounds.height), // bottom
            new Values(position.x, cy), // left
        ];
    }

    getBestAnchorPoint(target: AbstractShape, position: Values, targetPos: Values, anchor: Anchor): { point: Values, index: number } {
        let bounds = this.currentBounding ? this.currentBounding : this.toShapeBounding();
        const anchors = this.getRectangleAnchorPoints(position);
        switch (anchor) {
            case Anchor.TOP:
                return { point: anchors[0], index: 0 }; // Top anchor
            case Anchor.BOTTOM:
                return { point: anchors[2], index: 2 }; // Bottom anchor
            case Anchor.LEFT:
                return { point: anchors[3], index: 3 }; // Left anchor
            case Anchor.RIGHT:
                return { point: anchors[1], index: 1 }; // Right anchor
            default:
                break;
        }
        let targetBounds = target.currentBounding ? target.currentBounding : target.toShapeBounding();
        const targetCenter = new Values(targetPos.x + targetBounds.width / 2, targetPos.y + targetBounds.height / 2);
        const selfCenter = new Values(position.x + bounds.width / 2, position.y + bounds.height / 2);

        const dx = targetCenter.x - selfCenter.x;
        const dy = targetCenter.y - selfCenter.y;

        let index = 0;
        if (Math.abs(dx) > Math.abs(dy)) {
            index = dx > 0 ? 1 : 3; // right or left
        } else {
            index = dy > 0 ? 2 : 0; // bottom or top
        }
        return { point: anchors[index], index };
    }

    getChildrenBounds(): { x: number, y: number, width: number, height: number } {
        if (this.children.length === 0) return { x: 0, y: 0, width: 0, height: 0 };

        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const child of this.children) {
            minX = Math.min(minX, child.x);
            minY = Math.min(minY, child.y);
            maxX = Math.max(maxX, child.x + child.width);
            maxY = Math.max(maxY, child.y + child.height);
        }

        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        };
    }

    protected wrapText(
        ctx: CanvasRenderingContext2D,
        text: string,
        maxWidth: number,
        maxLines?: number
    ): string[] {
        const words = text.trim().split(/\s+/).filter(Boolean);
        const lines: string[] = [];
        let currentLine = '';
        const appendLine = (line: string) => {
            if (!line) return true;
            lines.push(line);
            return !maxLines || lines.length < maxLines;
        };

        for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            if (ctx.measureText(testLine).width <= maxWidth) {
                currentLine = testLine;
                continue;
            }

            if (currentLine && !appendLine(currentLine)) {
                return this.withEllipsis(ctx, lines, maxWidth);
            }

            // A single unbroken token (for example an identifier) must never
            // overflow its card. Split it before applying an ellipsis policy.
            if (ctx.measureText(word).width > maxWidth) {
                let remainder = word;
                while (remainder) {
                    let sliceLength = remainder.length;
                    while (sliceLength > 1 && ctx.measureText(remainder.slice(0, sliceLength)).width > maxWidth) {
                        sliceLength--;
                    }
                    const slice = remainder.slice(0, sliceLength);
                    remainder = remainder.slice(sliceLength);
                    if (remainder) {
                        if (!appendLine(slice)) return this.withEllipsis(ctx, lines, maxWidth);
                    } else {
                        currentLine = slice;
                    }
                }
            } else {
                currentLine = word;
            }
        }

        if (currentLine) {
            if (maxLines && lines.length >= maxLines) {
                return this.withEllipsis(ctx, lines, maxWidth);
            }
            lines.push(currentLine);
        }

        return lines;
    }

    protected withEllipsis(ctx: CanvasRenderingContext2D, lines: string[], maxWidth: number): string[] {
        if (!lines.length) return ['...'];
        const result = [...lines];
        result[result.length - 1] = this.addEllipsis(ctx, result[result.length - 1], maxWidth);
        return result;
    }

    protected addEllipsis(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
        if (ctx.measureText(text + '...').width <= maxWidth) return text + '...';
        while (ctx.measureText(text + '...').width > maxWidth && text.length > 1) {
            text = text.slice(0, -1);
        }
        return text.trim() + '...';
    }

    abstract draw(ctx: CanvasRenderingContext2D): void;
    abstract drawOnTop(ctx: CanvasRenderingContext2D): void;
}
